import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import ISonicChatroom from "../../common/classes/SonicChatroom";
import { SonicMessage } from "../../common/classes/SonicMessage";
import { getChatRooms } from "../../services/api/chat/getChatrooms";
import SocketClientEvents from "../../services/socket/SocketClientEvents";
import { getChatSocket } from "../../services/socket/useSocket";
import { RootState } from "../store";

interface ChatState {
  rooms: Array<ISonicChatroom>;
  notifications: { [roomId: string]: number };
  latestMessages: { [roomId: string]: SonicMessage };
}

const initialState: ChatState = {
  rooms: [],
  notifications: {},
  latestMessages: {},
};

export const getRooms = createAsyncThunk(
  "chat/getRoomsStatus",
  async (authToken: string) => {
    const res = await getChatRooms(authToken);
    return res;
  }
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setLatestMessage: (
      state: ChatState,
      action: PayloadAction<SonicMessage>
    ) => {
      const l = { ...state.latestMessages };
      l[action.payload.roomId] = action.payload;
      state.latestMessages = l;
    },
    setNotifications: (
      state: ChatState,
      action: PayloadAction<{ [roomId: string]: number }>
    ) => {
      state.notifications = action.payload;
    },
    addNotification: (
      state: ChatState,
      action: PayloadAction<SonicMessage>
    ) => {
      const n = { ...state.notifications };
      const l = { ...state.latestMessages };
      n[action.payload.roomId] = n[action.payload.roomId] + 1;
      l[action.payload.roomId] = action.payload;
      state.notifications = n;
      state.latestMessages = l;
    },
    removeNotification: (state: ChatState, action: PayloadAction<string>) => {
      const n = { ...state.notifications };
      n[action.payload] = n[action.payload] - 1;

      state.notifications = n;
    },
    addRoom: (state: ChatState, action: PayloadAction<ISonicChatroom>) => {
      const n = { ...state.notifications };
      const r = [...state.rooms];
      const exist = r.findIndex((el) => el.id === action.payload.id);
      if (exist === -1) {
        n[action.payload.id] = 0;
        r.push({ ...action.payload });
      }
      state.rooms = r;
      state.notifications = n;
    },
    clearChatState: (state) => {
      state.notifications = {};
      state.latestMessages = {};
      state.rooms = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRooms.pending, (state) => {
        state.rooms = [];
        state.notifications = {};
        state.latestMessages = {};
      })
      .addCase(getRooms.rejected, (state) => {
        state.rooms = [];
        state.notifications = {};
        state.latestMessages = {};
      })
      .addCase(getRooms.fulfilled, (state, action) => {
        if (!action.payload) return;
        const socket = getChatSocket();
        const rooms: Array<ISonicChatroom> = [];
        const notificationsInfo: { [roomId: string]: number } = {};
        const latestMessagesInfo: {
          [roomId: string]: SonicMessage;
        } = {};

        action.payload.forEach(({ room, unreadCount, latestMessage }) => {
          rooms.push(room);
          notificationsInfo[room.id] = unreadCount;
          if (latestMessage) {
            latestMessagesInfo[room.id] = latestMessage;
          }
          socket.emit(SocketClientEvents.joinRoom, room.id);
        });
        state.rooms = rooms;
        state.notifications = notificationsInfo;
        state.latestMessages = latestMessagesInfo;
      });
  },
});

export const {
  setLatestMessage,
  setNotifications,
  addNotification,
  removeNotification,
  addRoom,
  clearChatState,
} = chatSlice.actions;
export default chatSlice.reducer;

export const getStateChatRooms = (state: RootState) => state.chat.rooms;
export const getNotifications = (state: RootState) => state.chat.notifications;
export const getNotificationsTotal = (state: RootState) => {
  let n = 0;

  for (const key in state.chat.notifications) {
    n += state.chat.notifications[key];
  }

  return n;
};
export const getLatestMessages = (state: RootState) =>
  state.chat.latestMessages;
